import { formatDateOnly } from './taskDates';

export const TASK_STATUSES = ['TODO', 'IN_PROGRESS', 'IN_REVIEW', 'BLOCKED', 'DONE'] as const;

export type TaskStatus = (typeof TASK_STATUSES)[number];

/**
 * Display order is this array, never the enum's own ordering: Postgres appends
 * new enum values to the end of its sort order, so a state added mid-flow later
 * would sort out of place.
 *
 * Names are prefixed rather than reusing STATUS_LABELS, because a project's
 * status badge and a task's are rendered in the same page tree and unprefixed
 * twins invite an import mix-up the compiler would not catch.
 */
export const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  TODO: 'To do',
  IN_PROGRESS: 'In progress',
  IN_REVIEW: 'In review',
  BLOCKED: 'Blocked',
  DONE: 'Done',
};

/** Muted fills so a list of tasks does not turn into a colour chart. */
export const TASK_STATUS_BADGE_CLASSES: Record<TaskStatus, string> = {
  TODO: 'bg-stone-100 text-stone-700',
  IN_PROGRESS: 'bg-sky-100 text-sky-700',
  IN_REVIEW: 'bg-violet-100 text-violet-700',
  BLOCKED: 'bg-amber-100 text-amber-700',
  DONE: 'bg-emerald-100 text-emerald-700',
};

/**
 * The same five hues as the badges, at full strength.
 *
 * A badge is a filled pill and can carry a pale wash; a 8px dot at that
 * lightness is invisible, so the dot map is its own thing rather than something
 * derived from TASK_STATUS_BADGE_CLASSES.
 */
export const TASK_STATUS_DOT_CLASSES: Record<TaskStatus, string> = {
  TODO: 'bg-stone-400',
  IN_PROGRESS: 'bg-sky-500',
  IN_REVIEW: 'bg-violet-500',
  BLOCKED: 'bg-amber-500',
  DONE: 'bg-emerald-500',
};

/**
 * Everything except DONE -- what a task list asks for while completed work is
 * hidden.
 *
 * Derived from TASK_STATUSES rather than written out, so a status added later
 * is unfinished by default. That is the safe direction: a new state nobody has
 * taught these pages about should show up, not quietly disappear.
 */
export const UNFINISHED_STATUSES = TASK_STATUSES.filter((status) => status !== 'DONE');

export function isTaskStatus(value: string | null): value is TaskStatus {
  return value !== null && (TASK_STATUSES as readonly string[]).includes(value);
}

/**
 * The next state when a task is advanced by one click, or null when there is
 * nowhere further to go.
 *
 * Deliberately not `TASK_STATUSES[index + 1]`. That array is a *display* order,
 * and BLOCKED sorts between IN_REVIEW and DONE -- stepping the index would make
 * the button mean "go backwards" on every task in review. BLOCKED is a state a
 * task falls into sideways rather than one it advances into, so it is not on
 * the path at all; advancing out of it resumes at IN_PROGRESS, which is what
 * unblocking means. It stays reachable from the bulk bar and the task panel.
 *
 * DONE returns null rather than wrapping round to TODO: a control whose whole
 * job is to finish a task must not un-finish it on the very next click.
 *
 * A Record rather than a lookup with a fallback, so adding a status is a type
 * error here instead of a silently wrong `?? null`.
 */
const NEXT_TASK_STATUS: Record<TaskStatus, TaskStatus | null> = {
  TODO: 'IN_PROGRESS',
  IN_PROGRESS: 'IN_REVIEW',
  IN_REVIEW: 'DONE',
  BLOCKED: 'IN_PROGRESS',
  DONE: null,
};

export const nextTaskStatus = (status: TaskStatus): TaskStatus | null => NEXT_TASK_STATUS[status];

/**
 * The toast a row's status change raises, which carries its Undo.
 *
 * Shared by the Tasks pages and the dashboard's cards so the same move reads
 * the same wherever it was made. The task panel does not raise one: the panel
 * keeps the status on screen in a control you can just set back.
 */
export function statusChangedMessage(
  title: string,
  status: TaskStatus,
  /** The task's next instance, when finishing it spawned one -- the toast is
   *  the one place that says where the work went. */
  spawned?: { dueDate: string | null },
): string {
  const moved = `“${title}” moved to ${TASK_STATUS_LABELS[status]}`;
  return spawned?.dueDate ? `${moved}. Next due ${formatDateOnly(spawned.dueDate)}` : moved;
}

export function bulkStatusChangedMessage(count: number, status: TaskStatus): string {
  return `${count} ${count === 1 ? 'task' : 'tasks'} moved to ${TASK_STATUS_LABELS[status]}`;
}
